import { apiFetchJSON } from '../../core/api.js';
import { getIsOwnerApp } from '../../app/app-state.js';
import type {
  AppendOwnerChatMessagesOptions,
  OwnerChatMessage,
  OwnerChatMessagesResponse
} from '../../types/api.js';
import { getOwnerChatActiveConversationUserId, getOwnerChatAutoScrollEnabled, getOwnerChatMode, getOwnerChatSelectedDisplayName, setOwnerChatAutoScrollEnabled } from './ui-state.js';
import { scrollOwnerChatToBottom, scrollOwnerChatToBottomSmooth } from './scroll.js';
import { getOwnerChatViewerId, ownerChatMessagesEl } from './dom.js';
import {
  getOwnerChatLastSeenId,
  setOwnerChatLastSeenId,
  resetOwnerChatLastSeenId
} from './session.js';
import {
  createOwnerChatPhotoBlock,
  ownerChatPhotoProgressByMsgId,
  xhrAuthedBlob
} from './photo-progress.js';

const authedImageBlobCache = new Map<string, string>();

function updateOwnerChatAutoScrollEnabled() {
  if (!ownerChatMessagesEl) return;
  const maxTop = Math.max(0, ownerChatMessagesEl.scrollHeight - ownerChatMessagesEl.clientHeight);
  const remaining = maxTop - ownerChatMessagesEl.scrollTop;
  // Небольшой порог, чтобы из-за дробных пикселей режим не "дребезжал".
  setOwnerChatAutoScrollEnabled(remaining <= 20);
}

if (ownerChatMessagesEl) {
  ownerChatMessagesEl.addEventListener('scroll', function () {
    updateOwnerChatAutoScrollEnabled();
  }, { passive: true });
}

export function formatOwnerChatTime(ts: number | string) {
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch (_) {
    return '';
  }
}

export function clearOwnerChatMessages() {
  if (!ownerChatMessagesEl) return;
  ownerChatMessagesEl.innerHTML = '';
}

export function removeOwnerChatMessageById(msgId: number | string | null | undefined) {
  if (!ownerChatMessagesEl || msgId == null) return;
  var row = ownerChatMessagesEl.querySelector(
    '.chat-msg-row[data-msg-id="' + String(msgId) + '"]'
  );
  if (row) row.remove();
}

export function updateOwnerChatMessageInDom(message: OwnerChatMessage): boolean {
  if (!ownerChatMessagesEl || !message || message.id == null) return false;
  const row = ownerChatMessagesEl.querySelector(
    '.chat-msg-row[data-msg-id="' + String(message.id) + '"]'
  );
  if (!row) return false;

  const bubble = row.querySelector('.chat-bubble');
  if (!bubble) return false;

  const textValue = String(message.text || '');
  const timeStr = message.createdAt ? formatOwnerChatTime(message.createdAt) : '';
  const editedAt = message.editedAt ? Number(message.editedAt) : 0;

  const oldInner = bubble.querySelector('.chat-bubble-inner');
  if (oldInner) oldInner.remove();

  if (textValue || timeStr) {
    bubble.appendChild(createOwnerChatBubbleInner(textValue, timeStr, editedAt || undefined));
  }

  return true;
}

function loadOwnerChatPhotoInBlock(
  block: ReturnType<typeof createOwnerChatPhotoBlock>,
  photoUrl: string,
  options?: { fromPct?: number }
) {
  const raw = String(photoUrl || '');
  if (!raw) return;
  const fromPct = options && options.fromPct != null ? options.fromPct : 0;
  const span = Math.max(1, 100 - fromPct);

  if (raw.indexOf('blob:') === 0 || raw.indexOf('data:') === 0) {
    block.setProgress(100);
    block.reveal(raw);
    return;
  }

  const cached = authedImageBlobCache.get(raw);
  if (cached) {
    block.setProgress(100);
    block.reveal(cached);
    return;
  }

  block.setProgress(fromPct);
  void xhrAuthedBlob(raw, function (pct) {
    block.setProgress(fromPct + Math.round((pct / 100) * span));
  })
    .then(function (result) {
      if (!result.ok || !result.blobUrl) {
        block.fail();
        return;
      }
      authedImageBlobCache.set(raw, result.blobUrl);
      block.setProgress(100);
      block.reveal(result.blobUrl);
    })
    .catch(function () {
      block.fail();
    });
}

export function upgradePendingOwnerChatMessage(
  pendingId: number | string,
  message: OwnerChatMessage,
  localPreviewUrl?: string
) {
  if (!ownerChatMessagesEl || pendingId == null || !message) return false;
  var row = ownerChatMessagesEl.querySelector(
    '.chat-msg-row[data-msg-id="' + String(pendingId) + '"]'
  );
  if (!row) return false;
  row.setAttribute('data-msg-id', String(message.id));
  var timeEl = row.querySelector('.chat-bubble-time');
  if (timeEl && message.createdAt) timeEl.textContent = formatOwnerChatTime(message.createdAt);
  if (message.photoUrl) {
    const block = ownerChatPhotoProgressByMsgId(String(message.id)) || ownerChatPhotoProgressByMsgId(pendingId);
    if (!block) return true;
    const serverUrl = String(message.photoUrl);
    const local = String(localPreviewUrl || '');
    if (local && (local.indexOf('blob:') === 0 || local.indexOf('data:') === 0)) {
      authedImageBlobCache.set(serverUrl, local);
      block.setProgress(100);
      block.reveal(local);
      return true;
    }
    loadOwnerChatPhotoInBlock(block, serverUrl, { fromPct: 96 });
  }
  return true;
}

const OWNER_CHAT_DISPLAY_NAME = 'Специалист';

function ownerChatDisplayInitials(displayName: string): string {
  if (displayName === OWNER_CHAT_DISPLAY_NAME) return 'С';
  return 'В';
}

function createOwnerChatBubbleInner(textValue: string, timeStr: string, editedAt?: number): HTMLDivElement {
  const inner = document.createElement('div');
  inner.className = 'chat-bubble-inner';
  if (!textValue && timeStr) inner.classList.add('chat-bubble-inner--time-only');

  if (textValue) {
    const text = document.createElement('div');
    text.className = 'chat-bubble-text';
    text.textContent = textValue;
    inner.appendChild(text);
  }

  if (timeStr) {
    const metaWrap = document.createElement('span');
    metaWrap.className = 'chat-bubble-meta';
    if (editedAt) {
      const editedEl = document.createElement('span');
      editedEl.className = 'chat-bubble-edited';
      editedEl.textContent = 'изм.';
      metaWrap.appendChild(editedEl);
    }
    const timeEl = document.createElement('span');
    timeEl.className = 'chat-bubble-time';
    timeEl.textContent = timeStr;
    metaWrap.appendChild(timeEl);
    inner.appendChild(metaWrap);
  }

  return inner;
}

interface OwnerChatRowBuildParams {
  msgId: string;
  isMe: boolean;
  displayName: string;
  initials: string;
  text?: string;
  photoUrl?: string;
  photoLoading?: boolean;
  orderId?: string;
  createdAt?: number | string;
  editedAt?: number;
}

function buildOwnerChatMessageRow(params: OwnerChatRowBuildParams): HTMLDivElement {
  const {
    msgId,
    isMe,
    displayName,
    initials,
    text = '',
    photoUrl = '',
    photoLoading = false,
    orderId = '',
    createdAt,
    editedAt
  } = params;
  const timeStr = createdAt ? formatOwnerChatTime(createdAt) : '';
  const textValue = String(text || '');

  const row = document.createElement('div');
  row.className = isMe ? 'chat-msg-row chat-msg-row-me' : 'chat-msg-row chat-msg-row-them';
  row.setAttribute('data-msg-id', msgId);

  if (!isMe) {
    const avatar = document.createElement('div');
    avatar.className = 'chat-avatar';
    avatar.textContent = initials;
    row.appendChild(avatar);
  }

  const contentCol = document.createElement('div');
  contentCol.className = 'chat-msg-col';

  if (!isMe && displayName) {
    const meta = document.createElement('div');
    meta.className = 'chat-msg-meta';
    const nameEl = document.createElement('div');
    nameEl.className = 'chat-msg-name';
    nameEl.textContent = displayName;
    meta.appendChild(nameEl);
    contentCol.appendChild(meta);
  }

  const bubble = document.createElement('div');
  bubble.className = isMe ? 'chat-bubble chat-bubble-me' : 'chat-bubble chat-bubble-them';

  if (orderId && getOwnerChatMode() === 'consult') {
    const tag = document.createElement('div');
    tag.className = 'chat-order-tag';
    const short = orderId.split('-').pop() || orderId.slice(-8);
    tag.textContent = 'Заказ #' + short;
    bubble.appendChild(tag);
  }

  if (photoUrl || photoLoading) {
    const block = createOwnerChatPhotoBlock();
    if (photoLoading && !photoUrl) block.setProgress(0);
    else if (photoUrl) loadOwnerChatPhotoInBlock(block, String(photoUrl));
    bubble.appendChild(block.wrap);
  }

  if (textValue || timeStr) {
    bubble.appendChild(createOwnerChatBubbleInner(textValue, timeStr, editedAt));
  }

  contentCol.appendChild(bubble);
  row.appendChild(contentCol);
  return row;
}

export function appendOwnerChatMessages(messages: OwnerChatMessage[], options?: AppendOwnerChatMessagesOptions) {
  if (!ownerChatMessagesEl) return;
  const animateIn = !options || options.animate !== false;
  const instantScroll = !!(options && options.instantScroll);
  const fadeOnly = !!(options && options.fadeOnly);
  const sendIn = !!(options && options.sendIn);
  const incoming = !!(options && options.incoming);
  const deferScroll = !!(options && options.deferScroll);
  const meId = getOwnerChatViewerId();
  const reducedMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Плавное появление нового + smooth-scroll (отправка и входящие по WS/опросу).
  const newOnlyRevealLift = animateIn && !fadeOnly && !reducedMotion && (sendIn || incoming);

  const existingIds = new Set<string>();
  ownerChatMessagesEl.querySelectorAll('.chat-msg-row[data-msg-id]').forEach(function (el) {
    const id = el.getAttribute('data-msg-id');
    if (id) existingIds.add(id);
  });

  const fragment = document.createDocumentFragment();
  const insertedRows: HTMLDivElement[] = [];

  messages.forEach((m, idx) => {
    const msgId = m && m.id != null ? Number(m.id) : null;
    if (msgId == null) return;
    const msgIdStr = String(msgId);
    if (existingIds.has(msgIdStr)) return;
    existingIds.add(msgIdStr);

    const isMe = meId && String(m.fromUserId) === meId;
    let displayName = 'Вы';
    if (!isMe) {
      if (m.fromRole === 'owner') {
        displayName = OWNER_CHAT_DISPLAY_NAME;
      } else if (getIsOwnerApp()) {
        const selected = String(getOwnerChatSelectedDisplayName() || '').trim();
        const fromUsername = m.fromUsername ? String(m.fromUsername).trim() : '';
        displayName = fromUsername || selected || 'Клиент';
      }
    }
    const initials = ownerChatDisplayInitials(displayName);

    const row = buildOwnerChatMessageRow({
      msgId: msgIdStr,
      isMe: !!isMe,
      displayName,
      initials,
      text: String(m.text || ''),
      photoUrl: m.photoUrl ? String(m.photoUrl) : '',
      photoLoading: !!(options && options.photoLoadingIds && options.photoLoadingIds.has(msgIdStr)),
      orderId: m.orderId ? String(m.orderId) : '',
      createdAt: m.createdAt,
      editedAt: m.editedAt ? Number(m.editedAt) : undefined
    });

    if (animateIn && !newOnlyRevealLift) {
      if (sendIn && isMe) row.classList.add('chat-msg-row--send-in');
      else {
        row.classList.add('chat-msg-row--animate-in');
        if (fadeOnly) row.classList.add('chat-msg-row--fade-only');
      }
      row.style.animationDelay = Math.min(idx, 8) * 0.035 + 's';
    }

    fragment.appendChild(row);
    insertedRows.push(row);
  });

  if (insertedRows.length === 0) return;
  ownerChatMessagesEl.appendChild(fragment);

  if (newOnlyRevealLift) {
    try {
      const firstRow = insertedRows[0];
      const rowHeight = firstRow
        ? (firstRow.getBoundingClientRect().height || firstRow.offsetHeight || 0)
        : 0;
      const liftPx = Math.max(12, Math.round(rowHeight + 10));

      insertedRows.forEach(function (row) {
        row.style.transition = 'none';
        row.style.willChange = 'transform, opacity';
        row.style.opacity = '0';
        row.style.transform = 'translateY(' + liftPx + 'px)';
      });

      if (getOwnerChatAutoScrollEnabled()) scrollOwnerChatToBottomSmooth(520);

      requestAnimationFrame(function () {
        try {
          insertedRows.forEach(function (row) {
            row.style.transition = 'transform 0.52s cubic-bezier(0.18, 0.95, 0.2, 1), opacity 0.42s ease';
            row.style.transform = 'translateY(0)';
            row.style.opacity = '1';
          });
        } catch (_) {}
      });

      setTimeout(function () {
        try {
          insertedRows.forEach(function (row) {
            row.style.transition = '';
            row.style.transform = '';
            row.style.opacity = '';
            row.style.willChange = '';
          });
        } catch (_) {}
      }, 620);
    } catch (_) {}
  }

  requestAnimationFrame(function () {
    requestAnimationFrame(function () {
      if (!getOwnerChatAutoScrollEnabled()) return;
      if (animateIn) {
        if (newOnlyRevealLift || deferScroll) return;
        if (instantScroll) scrollOwnerChatToBottom();
        else scrollOwnerChatToBottomSmooth();
      } else scrollOwnerChatToBottom();
      updateOwnerChatAutoScrollEnabled();
    });
  });
}

const CONSULT_WELCOME_TEXT =
  'Здравствуйте! Можете задать любые вопросы по теме ПК — подбор комплектующих, сборка, апгрейд, совместимость. Ответим и поможем с выбором.';

const CONSULT_WELCOME_AT_KEY_PREFIX = 'ddpc_consult_welcome_at_';

function getConsultWelcomeCreatedAt(): number {
  const viewerId = getOwnerChatViewerId();
  if (!viewerId) return Date.now();
  const key = CONSULT_WELCOME_AT_KEY_PREFIX + viewerId;
  try {
    const raw = localStorage.getItem(key);
    const stored = raw ? Number(raw) : 0;
    if (stored > 0) return stored;
    const now = Date.now();
    localStorage.setItem(key, String(now));
    return now;
  } catch {
    return Date.now();
  }
}

export function ownerChatHasConsultWelcome(): boolean {
  return !!(ownerChatMessagesEl && ownerChatMessagesEl.querySelector('[data-consult-welcome="1"]'));
}

export function showConsultWelcomeBubble(): void {
  if (!ownerChatMessagesEl || ownerChatHasConsultWelcome()) return;

  const row = buildOwnerChatMessageRow({
    msgId: 'consult-welcome',
    isMe: false,
    displayName: OWNER_CHAT_DISPLAY_NAME,
    initials: 'С',
    text: CONSULT_WELCOME_TEXT,
    createdAt: getConsultWelcomeCreatedAt()
  });
  row.classList.add('chat-msg-row--animate-in', 'chat-msg-row--fade-only');
  row.setAttribute('data-consult-welcome', '1');

  ownerChatMessagesEl.insertBefore(row, ownerChatMessagesEl.firstChild);
}

function ensureConsultWelcome(): void {
  if (getIsOwnerApp()) return;
  if (getOwnerChatMode() !== 'consult') return;
  showConsultWelcomeBubble();
}

export async function loadOwnerChatMessages(options: { reset?: boolean; fromPoll?: boolean } = {}) {
  const { reset = false, fromPoll = false } = options;
  if (!getOwnerChatViewerId() || !getOwnerChatActiveConversationUserId()) return;
  if (!ownerChatMessagesEl) return;

  if (reset) {
    resetOwnerChatLastSeenId();
    clearOwnerChatMessages();
  }

  const sinceId = getOwnerChatLastSeenId() ? Number(getOwnerChatLastSeenId()) : 0;
  const data = await apiFetchJSON<OwnerChatMessagesResponse>(
    `/api/owner-chat/messages?conversationUserId=${encodeURIComponent(getOwnerChatActiveConversationUserId()!)}&sinceId=${encodeURIComponent(String(sinceId))}`,
    { method: 'GET' }
  ).catch(() => null);

  if (!data || !data.ok || !Array.isArray(data.messages)) {
    if (reset) ensureConsultWelcome();
    return;
  }

  if (data.messages.length === 0) {
    if (reset) ensureConsultWelcome();
    return;
  }

  setOwnerChatLastSeenId(data.messages[data.messages.length - 1].id || sinceId);
  // При открытии чата делаем плавное "загорание" всех сообщений,
  // но скролл внизу — без smooth, чтобы не было лишних перемещений.
  appendOwnerChatMessages(data.messages, {
    animate: true,
    instantScroll: !!reset || fromPoll,
    fadeOnly: !!reset,
    incoming: !reset && !!fromPoll,
    deferScroll: !!fromPoll
  });
  if (reset) ensureConsultWelcome();
}
